import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  CircularProgress,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  previewPartialDelivery,
  isCodPayment,
  addCalendarDay,
  UNAVAILABLE_REASONS,
} from '../../utils/partialDelivery';
import api from '../../utils/api';
import { useApp } from '../../context/AppContext';

const STEPS = ['Ready to ship', 'Remaining items', 'Review'];

function slotLabel(slot) {
  if (!slot) return '';
  if (slot.displayTime) return slot.displayTime;
  if (slot.startTime && slot.endTime) return `${slot.startTime} - ${slot.endTime}`;
  return slot.id || '';
}

function originalSlotLabel(order) {
  return slotLabel(order?.timeSlotDetails) || order?.timeSlotId || 'Original slot';
}

function formatRs(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '₹0.00';
  return `₹${n.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDisplayDate(dateStr) {
  if (dateStr == null || dateStr === '') return '';
  const match = String(dateStr).trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return String(dateStr);
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function lineSku(product) {
  return product?.variant?.sku != null ? String(product.variant.sku) : '';
}

function buildSlotDetails(slot) {
  if (!slot) return null;
  return {
    id: slot.id,
    startTime: slot.startTime,
    endTime: slot.endTime,
    displayTime: slot.displayTime,
    active: slot.active,
    minOrderQty: slot.minOrderQty || 0,
    currOrdersCount: slot.currOrdersCount || 0,
  };
}

/** Active, not-disabled slots. The server marks past/too-soon today slots isDisabled. */
function usableSlots(slots) {
  return (slots || []).filter((s) => s.active && !s.isDisabled);
}

const PartialDeliveryModal = ({ open, order, onClose, onCreated }) => {
  const { actions } = useApp();
  const products = order?.products || [];

  const [step, setStep] = useState(0);
  const [available, setAvailable] = useState([]);
  const [unavailQty, setUnavailQty] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [action, setAction] = useState('split');
  const [customerInformed, setCustomerInformed] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Remaining-items delivery: later today (evening) or tomorrow.
  const [remainingDay, setRemainingDay] = useState('tomorrow');
  const [todaySlots, setTodaySlots] = useState([]);
  const [todaySlotsLoading, setTodaySlotsLoading] = useState(false);
  const [tomorrowSlots, setTomorrowSlots] = useState([]);
  const [tomorrowSlotsLoading, setTomorrowSlotsLoading] = useState(false);
  const [selectedRemainingSlotId, setSelectedRemainingSlotId] = useState('');

  const tomorrowDate = useMemo(
    () => (order ? addCalendarDay(order.deliveryDate) : null),
    [order]
  );

  useEffect(() => {
    if (!open || !order) return;
    setStep(0);
    setAvailable(products.map(() => true));
    setUnavailQty(products.map((p) => Number(p.quantity) || 1));
    setReasons(products.map(() => 'out_of_stock'));
    setAction('split');
    setRemainingDay('tomorrow');
    setCustomerInformed(false);
    setRemarks('');
    setSubmitting(false);
    setTodaySlots([]);
    setTomorrowSlots([]);
    setSelectedRemainingSlotId('');
  }, [open, order]);

  const storeId = order?.storeId || order?.branch_id;

  // Fetch later-today slots (after now) for remaining items.
  useEffect(() => {
    if (!open || !order || step !== 1 || action !== 'split') return;
    if (!storeId || !order.deliveryDate) return;

    let cancelled = false;
    (async () => {
      try {
        setTodaySlotsLoading(true);
        const response = await api.get(
          `/time-slots?storeId=${storeId}&deliveryDate=${order.deliveryDate}`
        );
        if (cancelled) return;
        if (response.data?.success) {
          const slots = usableSlots(response.data.timeSlots);
          setTodaySlots(slots);
        }
      } catch (err) {
        if (!cancelled) actions.showSnackbar('Could not load later-today slots', 'warning');
      } finally {
        if (!cancelled) setTodaySlotsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, order, step, action, storeId, actions]);

  // Fetch tomorrow slots for remaining items.
  useEffect(() => {
    if (!open || !order || step !== 1 || action !== 'split') return;
    if (!storeId || !tomorrowDate) return;

    let cancelled = false;
    (async () => {
      try {
        setTomorrowSlotsLoading(true);
        const response = await api.get(
          `/time-slots?storeId=${storeId}&deliveryDate=${tomorrowDate}`
        );
        if (cancelled) return;
        if (response.data?.success) {
          const slots = usableSlots(response.data.timeSlots);
          setTomorrowSlots(slots);
        }
      } catch (err) {
        if (!cancelled) actions.showSnackbar('Could not load tomorrow’s time slots', 'warning');
      } finally {
        if (!cancelled) setTomorrowSlotsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, order, step, action, tomorrowDate, storeId, actions]);

  const remainingSlots = remainingDay === 'today' ? todaySlots : tomorrowSlots;
  const remainingSlotsLoading =
    remainingDay === 'today' ? todaySlotsLoading : tomorrowSlotsLoading;

  useEffect(() => {
    if (remainingDay === 'today' && !todaySlotsLoading && todaySlots.length === 0) {
      setRemainingDay('tomorrow');
    }
  }, [remainingDay, todaySlotsLoading, todaySlots.length]);

  // Keep the remaining-slot selection valid for the chosen day.
  useEffect(() => {
    if (action !== 'split') return;
    setSelectedRemainingSlotId((prev) => {
      if (prev && remainingSlots.some((s) => s.id === prev)) return prev;
      return remainingSlots[0]?.id || '';
    });
  }, [action, remainingDay, remainingSlots]);

  const unavailableItems = useMemo(() => {
    const items = [];
    products.forEach((product, index) => {
      if (available[index]) return;
      const lineQty = Number(product.quantity) || 1;
      const qty = Number(unavailQty[index]) || lineQty;
      items.push({
        index,
        productId: product.productId != null ? String(product.productId) : '',
        variantSku: lineSku(product),
        quantity: qty,
        reason: reasons[index] || 'out_of_stock',
      });
    });
    return items;
  }, [products, available, unavailQty, reasons]);

  const availableCount = available.filter(Boolean).length;
  const unavailableCount = available.length - availableCount;

  const preview = useMemo(() => {
    if (!order) return { canSubmit: false, children: [], refundAmount: 0 };
    try {
      return previewPartialDelivery(order, unavailableItems, action);
    } catch (err) {
      return { canSubmit: false, children: [], refundAmount: 0, error: err.message };
    }
  }, [order, unavailableItems, action]);

  const allReasonsPresent = unavailableItems.every(
    (item) => item.reason && String(item.reason).trim() !== ''
  );
  const qtysValid = unavailableItems.every((item) => {
    const lineQty = Number(products[item.index]?.quantity) || 0;
    return Number.isInteger(item.quantity) && item.quantity >= 1 && item.quantity <= lineQty;
  });

  const parentTotal = Number(order?.total) || 0;
  const isCod = isCodPayment(order);
  const child1 = preview.children?.[0];
  const child2 = preview.children?.[1];
  const remainingSlotObj = remainingSlots.find((s) => s.id === selectedRemainingSlotId);
  const remainingDate = remainingDay === 'today' ? order?.deliveryDate : tomorrowDate;

  const canProceedStep0 = availableCount >= 1 && unavailableCount >= 1;
  const remainingSlotOk =
    action !== 'split' || remainingSlots.length === 0 || !!selectedRemainingSlotId;
  const canProceedStep1 =
    preview.canSubmit && allReasonsPresent && qtysValid && remainingSlotOk;
  const canCreate = preview.canSubmit && customerInformed && canProceedStep1 && !submitting;

  const toggleAvailable = (index, checked) => {
    setAvailable((prev) => {
      const next = [...prev];
      next[index] = checked;
      return next;
    });
  };

  const handleNext = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const handleBack = () => setStep((s) => Math.max(s - 1, 0));

  const handleCreate = async () => {
    if (!canCreate || !order) return;
    setSubmitting(true);
    try {
      const orderId = order.orderId || order.id;
      const payload = {
        unavailableItems,
        action,
        remarks,
        customerInformed: true,
      };
      if (action === 'split' && selectedRemainingSlotId) {
        payload.remainingDeliveryDay = remainingDay;
        payload.remainingTimeSlotId = selectedRemainingSlotId;
        const details = buildSlotDetails(remainingSlotObj);
        if (details) payload.remainingTimeSlotDetails = details;
      }
      const response = await api.post(`/orders/${orderId}/partial`, payload);
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to create orders');
      }
      const ids = (response.data.childOrderIds || []).map((id) => `#${id}`).join(', ');
      actions.showSnackbar(`Created ${ids}`, 'success');
      onCreated?.(response.data);
      onClose();
    } catch (error) {
      actions.showSnackbar(
        error.response?.data?.message || error.message || 'Failed to create partial delivery',
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!order) return null;

  const availableProductList = products.filter((_, i) => available[i]);
  const unavailableProductList = products
    .map((product, index) => ({ product, index }))
    .filter(({ index }) => !available[index]);

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" component="span">
          Partial Delivery — #{order.orderId || order.id}
        </Typography>
        <IconButton onClick={onClose} disabled={submitting} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {step === 0 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Tick items that can go out on the original delivery. Untick items that are not
              available — those will be scheduled or refunded next.
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Original delivery: {formatDisplayDate(order.deliveryDate)} · {originalSlotLabel(order)}
            </Typography>

            {products.map((product, index) => {
              const lineQty = Number(product.quantity) || 1;
              const ticked = available[index] !== false;
              return (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                    p: 1.5,
                    mb: 1,
                    border: 1,
                    borderColor: ticked ? 'divider' : 'warning.light',
                    borderRadius: 1,
                    backgroundColor: ticked ? 'grey.50' : 'warning.50',
                  }}
                >
                  <FormControlLabel
                    sx={{ alignItems: 'flex-start', m: 0, flex: 1 }}
                    control={
                      <Checkbox
                        checked={ticked}
                        onChange={(e) => toggleAvailable(index, e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {product.name || 'Item'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Qty {lineQty}
                          {product.variant?.weight ? ` · ${product.variant.weight}` : ''}
                          {lineSku(product) ? ` · ${lineSku(product)}` : ''}
                          {` · ${formatRs(product.price)}`}
                        </Typography>
                      </Box>
                    }
                  />
                  <Chip
                    label={ticked ? 'Ready to ship' : 'Not available'}
                    size="small"
                    color={ticked ? 'success' : 'warning'}
                    variant={ticked ? 'filled' : 'outlined'}
                  />
                </Box>
              );
            })}

            {!canProceedStep0 && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Keep at least one item ready to ship and mark at least one as not available.
              </Typography>
            )}
          </Box>
        )}

        {step === 1 && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              These items are not going out on the original delivery
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add a reason, then choose whether the customer wants them delivered later or refunded.
            </Typography>

            {unavailableProductList.map(({ product, index }) => {
              const lineQty = Number(product.quantity) || 1;
              return (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 1.5,
                    mb: 1,
                    border: 1,
                    borderColor: 'warning.light',
                    borderRadius: 1,
                    backgroundColor: 'warning.50',
                    flexWrap: 'wrap',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 220 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {product.name || 'Item'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Qty {lineQty}
                      {lineSku(product) ? ` · ${lineSku(product)}` : ''}
                      {` · ${formatRs(product.price)}`}
                    </Typography>
                  </Box>
                  {lineQty > 1 && (
                    <TextField
                      label="Unavailable qty"
                      type="number"
                      size="small"
                      value={unavailQty[index] ?? lineQty}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        setUnavailQty((prev) => {
                          const next = [...prev];
                          next[index] = Number.isFinite(v) ? v : lineQty;
                          return next;
                        });
                      }}
                      inputProps={{ min: 1, max: lineQty, step: 1 }}
                      sx={{ width: 130 }}
                    />
                  )}
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Reason</InputLabel>
                    <Select
                      value={reasons[index] || 'out_of_stock'}
                      label="Reason"
                      onChange={(e) => {
                        const v = e.target.value;
                        setReasons((prev) => {
                          const next = [...prev];
                          next[index] = v;
                          return next;
                        });
                      }}
                    >
                      {UNAVAILABLE_REASONS.map((r) => (
                        <MenuItem key={r.value} value={r.value}>
                          {r.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              );
            })}

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              What does the customer want to do with these remaining items?
            </Typography>
            <FormControl sx={{ mb: 2 }}>
              <RadioGroup value={action} onChange={(e) => setAction(e.target.value)}>
                <FormControlLabel
                  value="split"
                  control={<Radio />}
                  label="Customer accepts — create a second order for these items"
                />
                <FormControlLabel
                  value="refund"
                  control={<Radio />}
                  label="Customer declined — refund these items"
                />
              </RadioGroup>
            </FormControl>

            {action === 'split' ? (
              <Box
                sx={{
                  p: 2,
                  border: 1,
                  borderColor: 'info.light',
                  borderRadius: 1,
                  backgroundColor: 'info.50',
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  When should the remaining items be delivered?
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  This slot is only for the second order (unavailable items). Ready-to-ship items
                  keep the original slot ({originalSlotLabel(order)}).
                </Typography>
                <RadioGroup
                  value={remainingDay}
                  onChange={(e) => setRemainingDay(e.target.value)}
                  sx={{ mb: 1.5 }}
                >
                  <FormControlLabel
                    value="today"
                    control={<Radio />}
                    disabled={todaySlotsLoading || todaySlots.length === 0}
                    label={
                      todaySlots.length === 0 && !todaySlotsLoading
                        ? 'Later today (no evening slots left)'
                        : `Later today — ${formatDisplayDate(order.deliveryDate)} (slots after now)`
                    }
                  />
                  <FormControlLabel
                    value="tomorrow"
                    control={<Radio />}
                    label={`Tomorrow — ${formatDisplayDate(tomorrowDate)}`}
                  />
                </RadioGroup>
                <FormControl size="small" fullWidth>
                  <InputLabel>Slot for remaining items</InputLabel>
                  <Select
                    value={selectedRemainingSlotId}
                    label="Slot for remaining items"
                    onChange={(e) => setSelectedRemainingSlotId(e.target.value)}
                    disabled={remainingSlotsLoading}
                  >
                    {remainingSlotsLoading && (
                      <MenuItem value="" disabled>
                        Loading…
                      </MenuItem>
                    )}
                    {!remainingSlotsLoading && remainingSlots.length === 0 && (
                      <MenuItem value="" disabled>
                        No slots available
                      </MenuItem>
                    )}
                    {remainingSlots.map((slot) => (
                      <MenuItem key={slot.id} value={slot.id}>
                        {slotLabel(slot)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            ) : (
              <Box
                sx={{
                  p: 2,
                  border: 2,
                  borderColor: isCod ? 'warning.main' : 'error.main',
                  borderRadius: 1,
                  backgroundColor: isCod ? 'warning.50' : 'error.50',
                }}
              >
                {isCod ? (
                  <>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Customer will pay {child1 ? formatRs(child1.total) : '—'} instead of{' '}
                      {formatRs(parentTotal)}
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      COD — nothing was collected. No refund to process.
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="h6" color="error.main" fontWeight="bold">
                      Refund due: {formatRs(preview.refundAmount)}
                    </Typography>
                    <Typography variant="caption" display="block">
                      Original {formatRs(parentTotal)} · Net after refund {formatRs(preview.netTotal)}
                    </Typography>
                  </>
                )}
              </Box>
            )}

            {preview.error && (
              <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                {preview.error}
              </Typography>
            )}
          </Box>
        )}

        {step === 2 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {action === 'split'
                ? 'Two orders will be created from this parent. Check the items, amounts, and slots below.'
                : 'One order will be created for the ready-to-ship items. The rest will be refunded.'}
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 240,
                  p: 2,
                  border: 1,
                  borderColor: 'success.light',
                  borderRadius: 1,
                  backgroundColor: 'success.50',
                }}
              >
                <Chip label="Order 1 — ready to ship" size="small" color="success" sx={{ mb: 1 }} />
                <Typography variant="h6">{child1 ? formatRs(child1.total) : '—'}</Typography>
                <Typography variant="caption" display="block">
                  Subtotal {child1 ? formatRs(child1.subtotal) : '—'}
                  {child1?.discount > 0 ? ` · Discount ${formatRs(child1.discount)}` : ''}
                  {child1?.shippingCost > 0 ? ` · Shipping ${formatRs(child1.shippingCost)}` : ''}
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                  Original delivery · {formatDisplayDate(order.deliveryDate)} · {originalSlotLabel(order)}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {availableProductList.map((p, i) => (
                    <Typography key={i} variant="caption" display="block" color="text.secondary">
                      • {p.name} × {Number(p.quantity) || 1}
                    </Typography>
                  ))}
                </Box>
              </Box>

              {action === 'split' ? (
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 240,
                    p: 2,
                    border: 1,
                    borderColor: 'info.light',
                    borderRadius: 1,
                    backgroundColor: 'info.50',
                  }}
                >
                  <Chip
                    label={
                      remainingDay === 'today'
                        ? 'Order 2 — remaining items (later today)'
                        : 'Order 2 — remaining items (tomorrow)'
                    }
                    size="small"
                    color="info"
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="h6">{child2 ? formatRs(child2.total) : '—'}</Typography>
                  <Typography variant="caption" display="block">
                    Subtotal {child2 ? formatRs(child2.subtotal) : '—'}
                    {child2?.discount > 0 ? ` · Discount ${formatRs(child2.discount)}` : ''}
                    {' · Shipping ₹0.00'}
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                    {formatDisplayDate(remainingDate)}
                    {remainingSlotObj ? ` · ${slotLabel(remainingSlotObj)}` : ''}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {unavailableProductList.map(({ product, index }) => (
                      <Typography key={index} variant="caption" display="block" color="text.secondary">
                        • {product.name} × {unavailQty[index] ?? (Number(product.quantity) || 1)}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 240,
                    p: 2,
                    border: 2,
                    borderColor: isCod ? 'warning.main' : 'error.main',
                    borderRadius: 1,
                    backgroundColor: isCod ? 'warning.50' : 'error.50',
                  }}
                >
                  {isCod ? (
                    <Typography variant="subtitle2" fontWeight="bold">
                      Remaining items cancelled (COD). Customer pays{' '}
                      {child1 ? formatRs(child1.total) : '—'} instead of {formatRs(parentTotal)}.
                    </Typography>
                  ) : (
                    <>
                      <Chip label="Refund — remaining items" size="small" color="error" sx={{ mb: 1 }} />
                      <Typography variant="h6" color="error.main" fontWeight="bold">
                        {formatRs(preview.refundAmount)}
                      </Typography>
                      <Typography variant="caption" display="block">
                        Original {formatRs(parentTotal)} · Net {formatRs(preview.netTotal)}
                      </Typography>
                    </>
                  )}
                </Box>
              )}
            </Box>

            <TextField
              label="Remarks"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              sx={{ mb: 2 }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={customerInformed}
                  onChange={(e) => setCustomerInformed(e.target.checked)}
                />
              }
              label="Customer has been informed"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} variant="outlined">
          Cancel
        </Button>
        {step > 0 && (
          <Button onClick={handleBack} disabled={submitting} variant="outlined">
            Back
          </Button>
        )}
        {step < STEPS.length - 1 ? (
          <Button
            onClick={handleNext}
            disabled={(step === 0 && !canProceedStep0) || (step === 1 && !canProceedStep1)}
            variant="contained"
          >
            Next
          </Button>
        ) : (
          <Button
            onClick={handleCreate}
            disabled={!canCreate}
            variant="contained"
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {submitting ? 'Creating…' : 'Create Orders'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PartialDeliveryModal;
